/**
 * Vingerafdruk van een productiebuild: de gehashte bestandsnamen van de entry-bundles
 * zoals index.html ze laadt (main/polyfills via `<script src>`, de globale styles via
 * `<link rel="stylesheet">`). Angular zet de content-hash in de naam (`main-WOARTMFB.js`),
 * en elke codewijziging cascadeert via de import-paden tot in main: een andere
 * vingerafdruk betekent dus "een refresh laadt iets anders dan wat deze tab draait".
 *
 * Bewust vergeleken met index.html en NIET met een versienummer: de backend rapporteert
 * een vaste assembly-versie, en een versie-bestand moet bij elke release mee bewegen.
 * index.html is per definitie wat een refresh zou laden.
 *
 * `ng serve` en `outputHashing: none` leveren ongehashte namen (`main.js`): dan is er
 * geen vingerafdruk en blijft de detectie uit, zodat lokaal nooit een update-dialoog
 * verschijnt.
 */
const HASHED_BUNDLE = /(?:^|\/)([\w.-]+-[A-Z0-9]{8}\.(?:js|css))(?:[?#].*)?$/;

/** Gesorteerde, ontdubbelde lijst gehashte entry-bundles in `root` (live document of geparste index.html). */
export function extractBuildFingerprint(root: ParentNode): string[] {
  const refs = Array.from(root.querySelectorAll('script[src], link[rel="stylesheet"][href]'))
    .map(el => el.getAttribute('src') ?? el.getAttribute('href') ?? '')
    .map(ref => HASHED_BUNDLE.exec(ref)?.[1])
    .filter((name): name is string => !!name);

  return [...new Set(refs)].sort();
}

export function parseBuildFingerprint(html: string): string[] {
  return extractBuildFingerprint(new DOMParser().parseFromString(html, 'text/html'));
}

/** Zonder main-bundle is het geen (herkenbare) productiebuild: foutpagina, dev-server, ... */
export function hasEntryBundle(fingerprint: readonly string[]): boolean {
  return fingerprint.some(name => name.startsWith('main-'));
}

/**
 * True wanneer `latest` een bundle bevat die de draaiende build niet kent. Een
 * deelverzameling telt niet als nieuwer: het live document kan in theorie extra
 * stylesheets bevatten die pas na het opstarten werden toegevoegd.
 */
export function isNewerBuild(running: readonly string[], latest: readonly string[]): boolean {
  if (!hasEntryBundle(running) || !hasEntryBundle(latest)) {
    return false;
  }

  return latest.some(name => !running.includes(name));
}

/**
 * Herkent het mislukken van een lazy chunk, de typische fout in een tab die de release
 * heeft overleefd: de oude gehashte chunks bestaan dan niet meer op de server.
 * - `import()`-fouten per browser: Chromium, Firefox, Safari (zone.js verpakt een
 *   onafgehandelde rejection als "Uncaught (in promise): TypeError: ...", vandaar geen anker);
 * - NG0750: een `@defer`-blok kon zijn chunks niet laden. In productie draagt die fout de
 *   oorspronkelijke import-fout niet mee, enkel de code.
 */
export function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : typeof error === 'string' ? error : '';
  return /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|NG0750\b/i.test(
    message
  );
}
